import { Stack } from '@cmg/design-system';
import { Form, FormikProvider, useFormik, yupToFormErrors } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useFirmKeyOrIdContext } from '../../../../../../contexts/FirmKeyOrIdContext';
import { useIndicationActivitySyndicateContext } from '../../../../context/IndicationActivitySyndicateContext';
import InvestorContactInformation from '../../../investor-contact-information/layout/InvestorContactInformation';
import { IndicationSectionWithCreateButtons } from '../../common/indication-section-create-view/IndicationSectionWithCreateButtons';
import IndicationSectionForm from '../../common/indication-section-form/IndicationSectionForm';
import { PassIndicationDialog } from '../../common/PassIndicationDialog';
import {
  createIndicationValidationSchema,
  CreateIndicationValidationSchemaContext,
  IndicationCreateFormValues,
} from '../../common/utils/IndicationSectionValidation';
import { createInitialValues } from '../../sales-and-trading/indication-section-create-view/IndicationSectionCreateView.model';
import { usePassOnInstitutionalIndication } from '../common/pass-institutional-indication-dialog/hooks/usePassOnInstitutionalIndication';
import { useCreateInstitutionalIndication } from './hooks/useCreateInstitutionalIndication';

export type Props = Readonly<{
  investorContactMandatory: boolean;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionCreateView: React.FC<Props> = ({ investorContactMandatory }) => {
  const { offeringId, basicPublishedOffering, loading, orderBookConfiguration, publishedOffering } =
    useIndicationActivitySyndicateContext();
  const offering = basicPublishedOffering!;
  const { firmKeyOrId } = useFirmKeyOrIdContext();

  const [isPassDialogOpen, setIsPassDialogOpen] = React.useState(false);

  const [createIndication] = useCreateInstitutionalIndication(offeringId);

  const formik = useFormik<IndicationCreateFormValues>({
    initialValues: createInitialValues(offering),
    validate: async values => {
      try {
        await createIndicationValidationSchema.validate(values, {
          abortEarly: false,
          context: {
            offeringType: offering.type,
            interestLevels: values.interestLevels,
            isLimitIndicationsOnlyEnabled: orderBookConfiguration.isLimitIndicationsOnlyEnabled,
            isIndicationPriceAsPercentageParEnabled:
              orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
            isOnlySharesUnitOnIndicationsEnabled:
              orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled,
          } satisfies CreateIndicationValidationSchemaContext,
        });

        return {};
      } catch (e) {
        return yupToFormErrors(e);
      }
    },
    onSubmit: createIndication,
  });
  const { isSubmitting, handleSubmit, handleReset, values } = formik;
  const isLoading = loading || isSubmitting;

  const [onPass, isPassing] = usePassOnInstitutionalIndication({
    offeringId,
    onClose: () => setIsPassDialogOpen(false),
    isPassingNewIndication: true,
  });

  return (
    <IndicationSectionWithCreateButtons
      isLoading={isLoading}
      handlePass={() => setIsPassDialogOpen(true)}
      handleReset={handleReset}
      handleSubmit={handleSubmit}
    >
      <Stack gap={1}>
        <FormikProvider value={formik}>
          <Form>
            <IndicationSectionForm
              securityType={defaultTo(publishedOffering?.security.type, null)}
              filings={defaultTo(publishedOffering?.terms.filings, null)}
              tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
              instruments={defaultTo(publishedOffering?.instruments, null)}
              issuerName={defaultTo(publishedOffering?.issuer.name, null)}
              offeringType={offering.type}
              canEnterTranche
              pricingCurrencyCode={offering.pricingCurrencyCode}
              securityTypeDisplayName={offering.securityTypeDisplayName!}
              isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
              isOnlySharesUnitOnIndicationsEnabled={
                orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
              }
              isIndicationPriceAsPercentageParEnabled={
                orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
              }
            />
          </Form>

          <InvestorContactInformation
            creating
            bankInvestorKey={firmKeyOrId}
            sellSideContacts={values.sellSideContacts}
            buySideContacts={EMPTY_ARRAY}
            isCoveredAccount={false}
            investorContactMandatory={investorContactMandatory}
          />
        </FormikProvider>

        <PassIndicationDialog
          isOpen={isPassDialogOpen}
          isLoading={isPassing}
          onClose={() => setIsPassDialogOpen(false)}
          onPass={onPass}
        />
      </Stack>
    </IndicationSectionWithCreateButtons>
  );
};

export default IndicationSectionCreateView;
